import { FC, MouseEvent, ReactNode } from 'react'
import { composeClasses } from '@/utils/compose-classes'
import { Spinner } from '@/components/spinner'

export interface OptionButtonProps {
  children?: ReactNode
  onClick?: (event: MouseEvent) => void
  isLoading?: boolean
  contentLoading?: ReactNode
  isDisabled?: boolean
  className?: string
  variant?: 'gray'
  size?: 'medium'
  rightIcon?: ReactNode
  leftIcon?: ReactNode
  onlyIcon?: ReactNode
  isActive?: boolean
  dataTestId?: string
}

const assignedColorsByVariant = {
  gray: 'text-black bg-transparent hover:bg-button-secondary active:bg-button-secondary-hover disabled:text-terciary disabled:hover:bg-button-secondary'
} as const

const assignedSize = {
  medium: 'text-body-bold-m'
} as const

export const OptionButton: FC<OptionButtonProps> = ({
  children,
  onClick,
  isLoading = false,
  isDisabled = false,
  contentLoading,
  className = '',
  variant = 'gray',
  size = 'medium',
  isActive = false,
  rightIcon,
  leftIcon,
  onlyIcon,
  dataTestId
}) => {
  if (!onlyIcon && !children) {
    throw new Error('OptionButton must have children or onlyIcon prop')
  }

  const variantClass = assignedColorsByVariant[variant]
  const sizeClass = assignedSize[size]

  return (
    <button
      data-testid={dataTestId}
      onClick={onClick}
      type="button"
      className={composeClasses(
        className,
        isActive && '!bg-white !text-primary !border-primary',
        variantClass,
        sizeClass,
        (isDisabled || isLoading) && 'pointer-events-none cursor-not-allowed',
        'p-3 gap-2 flex items-center justify-center border-2 border-transparent whitespace-nowrap duration-200 ease-out transition-colors flex-row rounded-[100px]'
      )}
      disabled={isDisabled}
    >
      {isLoading
        ? contentLoading ?? (
            <Spinner
              customBorderColor={
                isDisabled ? 'border-terciary' : 'border-black'
              }
            />
          )
        : onlyIcon ?? (
            <OptionButtonContent leftIcon={leftIcon} rightIcon={rightIcon}>
              {children}
            </OptionButtonContent>
          )}
    </button>
  )
}

type OptionButtonContentProps = Pick<
  OptionButtonProps,
  'leftIcon' | 'rightIcon' | 'children'
>

const OptionButtonContent = (props: OptionButtonContentProps) => {
  const { leftIcon, rightIcon, children } = props
  return (
    <>
      {leftIcon && <>{leftIcon}</>}
      {children}
      {rightIcon && <>{rightIcon}</>}
    </>
  )
}

export default OptionButton
